import {
  ArchivosDeExportacionSchema,
  CarpetaDeExportacionSchema,
  EstadoDeCarpetasSchema,
  ModeloDeCarpetaSchema,
  PruebaDeConexionSchema,
  ResultadoDeRevisionDeCarpetasSchema,
  ResultadoDeSubidaADropboxSchema,
  type ConfiguracionDeCarpetas,
} from '@cnc/contracts';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {json, pedir} from './cliente';

// Carpetas de las exportaciones en Dropbox (decision 0084).
export function useEstadoDeCarpetas() {
  return useQuery({queryKey: ['carpetas', 'estado'], queryFn: () => pedir('carpetas/estado', EstadoDeCarpetasSchema)});
}

export function useGuardarConfiguracionDeCarpetas() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (c: ConfiguracionDeCarpetas) => pedir('carpetas/configuracion', EstadoDeCarpetasSchema, json('PUT', c)),
    onSuccess: () => cliente.invalidateQueries({queryKey: ['carpetas']}),
  });
}

export function useProbarConexionDeCarpetas() {
  return useMutation({mutationFn: () => pedir('carpetas/probar', PruebaDeConexionSchema, json('POST'))});
}

export function useModeloDeCarpeta() {
  return useMutation({
    mutationFn: (ruta: string) => pedir('carpetas/modelo', ModeloDeCarpetaSchema, json('POST', {ruta})),
  });
}

export function useRevisarCarpetas() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: () => pedir('carpetas/revisar', ResultadoDeRevisionDeCarpetasSchema, json('POST')),
    onSuccess: () => cliente.invalidateQueries({queryKey: ['carpetas']}),
  });
}

// Donde estan las rutas de archivos de cada clase de exportacion: concentrados
// o doré (decision 0095).
export const RAIZ_DE_EXPORTACIONES = 'seguimiento/exportaciones';
export const RAIZ_DE_DORE = 'dore/exportaciones';

const rutaDe = (codigo: string, raiz: string) => `${raiz}/${encodeURIComponent(codigo)}/carpeta`;

export function useCarpetaDeExportacion(codigo: string, raiz = RAIZ_DE_EXPORTACIONES) {
  return useQuery({
    queryKey: ['carpetas', 'exportacion', codigo],
    queryFn: () => pedir(rutaDe(codigo, raiz), CarpetaDeExportacionSchema),
  });
}

export function useRevisarCarpetaDeExportacion(codigo: string, raiz = RAIZ_DE_EXPORTACIONES) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: () => pedir(rutaDe(codigo, raiz), CarpetaDeExportacionSchema, json('POST')),
    onSuccess: async datos => {
      cliente.setQueryData(['carpetas', 'exportacion', codigo], datos);
      await cliente.invalidateQueries({queryKey: ['carpetas', 'archivos', codigo]});
    },
  });
}

// Archivos de la carpeta de Dropbox de la exportacion (decision 0085).
const rutaDeArchivos = (codigo: string, raiz: string) => `${raiz}/${encodeURIComponent(codigo)}/archivos`;

export function useArchivosDeExportacion(codigo: string, raiz = RAIZ_DE_EXPORTACIONES) {
  return useQuery({
    queryKey: ['carpetas', 'archivos', codigo],
    queryFn: () => pedir(rutaDeArchivos(codigo, raiz), ArchivosDeExportacionSchema),
  });
}

// Direccion para abrir un archivo: la API redirige a un enlace de Dropbox.
export const enlaceParaAbrir = (codigo: string, ruta: string, raiz = RAIZ_DE_EXPORTACIONES) =>
  `/api/${rutaDeArchivos(codigo, raiz)}/abrir?ruta=${encodeURIComponent(ruta)}`;

// Vista previa dentro de la app (decision 0092): PDF, imagen, Word o Excel.
export const enlaceDeVistaPrevia = (codigo: string, ruta: string, raiz = RAIZ_DE_EXPORTACIONES) =>
  `/api/${rutaDeArchivos(codigo, raiz)}/vista?ruta=${encodeURIComponent(ruta)}`;

export function useSubirADropbox(codigo: string, raiz = RAIZ_DE_EXPORTACIONES) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({carpeta, archivos}: {carpeta: string; archivos: File[]}) => {
      const cuerpo = new FormData();
      for (const a of archivos) cuerpo.append('archivos', a, a.name);
      cuerpo.append('carpeta', carpeta);
      return pedir(rutaDeArchivos(codigo, raiz), ResultadoDeSubidaADropboxSchema, {method: 'POST', body: cuerpo});
    },
    onSuccess: () => cliente.invalidateQueries({queryKey: ['carpetas', 'archivos', codigo]}),
  });
}
